import {
    getOrderStatusAsString,
    getTicketStatusColor,
    InsuranceStatus,
    Ticket,
} from '@/models/ticket';
import { StajackTableColumn } from '@/components/Table/v2/Table';
import { LiftOperator } from '@/models/liftoperator';
import { Chip } from '@mui/material';
import { formatDateToString } from '@/components/TicketsOverviewUI/ticketUiUtils';

export const ticketTableColumnIds = [
    'orderReference',
    'email',
    'ticketOwnerData',
    'name',
    'orderDisplayId',
    'orderDate',
    'liftOperator',
    'ticketName',
    'status',
    'insurance',
] as const;

export type TicketTableColumnId = (typeof ticketTableColumnIds)[number];

type TicketTableColumn = {
    columnId: TicketTableColumnId;
} & StajackTableColumn<Ticket>;

export const getTicketColumns = ({
    liftOperators,
}: {
    liftOperators: LiftOperator[];
}): TicketTableColumn[] => [
    {
        label: 'Referenznummer',
        itemKey: 'orderReference',
        columnId: 'orderReference',
    },

    {
        label: 'E-Mail Buchender',
        itemKey: 'email',
        columnId: 'email',
    },

    {
        label: 'Ticketkäufer (+ Buchungsnummer)',
        itemTransform: (ticket) =>
            `${ticket.lastname} ${ticket.firstname}
                ${
                    ticket.confirmationNumber
                        ? ` (${ticket.confirmationNumber})`
                        : ''
                }
             `,
        columnId: 'ticketOwnerData',
    },

    {
        label: 'Starjack-Bestellnummer',
        itemKey: 'orderDisplayId',
        columnId: 'orderDisplayId',
    },

    {
        label: 'Bestellzeitpunkt',
        itemTransform: (ticket) =>
            formatDateToString(ticket.orderDate, { detailed: true }) || '-',
        columnId: 'orderDate',
    },

    {
        label: 'Bergbahn',
        itemTransform: (ticket) =>
            liftOperators.find((l) => l.id === ticket.liftOperatorId)?.name
                .de || '-',
        columnId: 'liftOperator',
    },

    {
        label: 'Ticketname',
        itemKey: 'name',
        columnId: 'ticketName',
    },

    {
        label: 'Schutz gebucht',
        columnId: 'insurance',
        itemTransform: (ticket) => (
            <Chip
                color={
                    ticket.insurance &&
                    ticket.insurance.status === InsuranceStatus.BOOKED
                        ? 'success'
                        : 'error'
                }
                label={getInsuranceLabel(ticket)}
            />
        ),
    },

    {
        label: 'Status',
        itemTransform: (ticket) => (
            <Chip
                color={getTicketStatusColor(ticket.status)}
                label={getOrderStatusAsString(ticket.status)}
            />
        ),
        columnId: 'status',
    },
];

const getInsuranceLabel = (ticket: Ticket) => {
    if (!ticket.insurance) return 'Nein';

    if (ticket.insurance.canceledAt) return 'Storniert';

    if (ticket.insurance.revokedAt) return 'Widerrufen';

    if (ticket.insurance.status === InsuranceStatus.BOOKED)
        return `Schutz (${ticket.insurance.tariffName.de}) gebucht`;
};
